import { PhosphorFileX, PhosphorIconForFileUpload } from "components/Atoms/PhosphorIcon";
import classNames from "classnames";
import React from "react";
import Dropzone, { Accept, DropEvent, FileRejection } from "react-dropzone";
import { formatBytes } from "shared/utils/fileutils";

interface FileDropzoneProps {
  error?: boolean;
  iconType: "image" | "file" | "docx" | "pdf";
  accept?: Accept;
  uploadLabel?: string;
  maxFiles?: number;
  maxSizeBytes: number;
  onDrop: (files: FileList | File[]) => void;
  onDropReject: (fileRejections: FileRejection[], event: DropEvent) => void;
}

interface PasteTarget {
  getRootEl: () => HTMLElement; // Needed to know if the dropzone is in view
  onDrop: (files: File[]) => void;
}

// We can ctrl+v into a dropzone. But if there are multiple dropzones in view, we don't know which one to use
// We use the set to keep track of all the dropzones in view
const pasteTargets = new Set<PasteTarget>();

const onDocumentPaste = (event: ClipboardEvent) => {
  const files = Array.from(event.clipboardData.files);
  if (files.length === 0) { return; }

  const target = event.target as HTMLElement;
  if (target?.closest?.(":read-write")) { return; }

  const inView = Array.from(pasteTargets).filter(entry => {
    const el = entry.getRootEl();
    if (!el) { return false; }
    const rect = el.getBoundingClientRect();
    return rect.top >= 0 && rect.left >= 0 && rect.top <= window.innerHeight && rect.left <= window.innerWidth;
  });
  if (inView.length !== 1) { return; }

  event.preventDefault();
  inView[0].onDrop(files);
};

export const FileDropzone = (props: FileDropzoneProps) => {
  const {
    iconType,
    accept,
    maxFiles = 0, // Unlimited number of files
    uploadLabel = RESX.Buttons.Upload,
    maxSizeBytes,
  } = props;

  const dropzoneRootRef = React.useRef<React.RefObject<HTMLElement>>(null);
  const onDropRef = React.useRef(props.onDrop);
  onDropRef.current = props.onDrop;

  React.useEffect(() => {
    const target = {
      getRootEl: () => dropzoneRootRef.current.current,
      onDrop: files => onDropRef.current(files),
    } satisfies PasteTarget;

    if (pasteTargets.size === 0) {
      document.addEventListener("paste", onDocumentPaste);
    }
    pasteTargets.add(target);

    return () => {
      pasteTargets.delete(target);
      if (pasteTargets.size === 0) {
        document.removeEventListener("paste", onDocumentPaste);
      }
    };
  }, []);

  return <Dropzone
    accept={accept}
    maxFiles={maxFiles}
    multiple={maxFiles !== 1}
    maxSize={maxSizeBytes}
    onDrop={props.onDrop}
    onDropRejected={props.onDropReject}
  >
    {({ getRootProps, getInputProps, isDragActive, isDragReject, rootRef }) => {
      dropzoneRootRef.current = rootRef;
      return <div
        style={{ width: "100%" }}
        {...getRootProps()}
        className={classNames("flex align-center justify-center dropzone padding-larger", {
          "dropzoneactive": isDragActive,
          "dropzoneerror": props.error,
        })}
      >
        <input {...getInputProps()} />
        <div className="flex-column align-center justify-center" style={{ minHeight: 68 }}>
          {!isDragReject && <>
            <div className="padding-small">
              <PhosphorIconForFileUpload type={iconType} error={props.error} />
            </div>
            <div className="margin-bottom-small">
              <span style={{ color: "var(--primary-color)" }}>
                {uploadLabel}
              </span>
              &nbsp;{RESX.File.orDragAndDrop}
            </div>
            <div className="text-sublabel">({RESX.File.MaxFileSize}: {formatBytes(maxSizeBytes)})</div>
          </>}
          {isDragReject && <>
            <div className="padding-small">
              <PhosphorFileX iconSize="large" />
            </div>
            <div className="margin-bottom-small">
              {RESX.File["Invalid file"]}
            </div>
          </>}
        </div>
      </div>;
    }}
  </Dropzone>
}